import { useCallback, useEffect, useRef, useState } from 'react';
import api, { apiError } from '../services/api';

/** GET `url` on mount and whenever it changes; `reload` re-fetches without clearing data. */
export function useApi<T>(url: string | null, params?: Record<string, unknown>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(Boolean(url));
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(params || {});
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!url) return;
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get<T>(url, { params: JSON.parse(key) });
      if (id === seq.current) setData(res);
    } catch (e) {
      if (id === seq.current) setError(apiError(e, 'Could not load data.'));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [url, key]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load, setData };
}
